import React, { useState } from "react";
import {
  Clock,
  CheckCircle,
  XCircle,
  Star,
  Users,
  Zap,
  ListChecks,
} from "lucide-react";
import { useVendorPerformance } from "../../services/vendorAnalyticsService";
import ChannelFilterTabs from "../../components/vendor/insights/ChannelFilterTabs";
import ChannelSplitCards from "../../components/vendor/insights/ChannelSplitCards";
import {
  VendorPageHeader,
  VendorGuideCallout,
} from "../../components/vendor/guidance";
import { VENDOR_PAGE_GUIDES } from "../../utils/vendorPageGuides";

const PERIOD_TABS = [
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" },
];

const VendorPerformance = () => {
  const [period, setPeriod] = useState("month");
  const [channel, setChannel] = useState("all");

  const { data, isLoading, error } = useVendorPerformance(period, channel);

  const performance = data?.data || {};

  const formatValue = (value, formatter) => {
    if (value === null || value === undefined || isNaN(value)) {
      return "N/A";
    }
    return formatter(value);
  };

  const performanceMetrics = [
    {
      label: "Response Time",
      value: formatValue(
        performance.response_time,
        (val) => `${Number(val).toFixed(1)} hrs`,
      ),
      icon: Clock,
      color: "text-ink",
      bgColor: "bg-surface-muted border-border",
    },
    {
      label: "Completion Rate",
      value: formatValue(
        performance.completion_rate,
        (val) => `${Number(val).toFixed(1)}%`,
      ),
      icon: CheckCircle,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50 border-emerald-100",
    },
    {
      label: "Cancellation Rate",
      value: formatValue(
        performance.cancellation_rate,
        (val) => `${Number(val).toFixed(1)}%`,
      ),
      icon: XCircle,
      color: "text-red-600",
      bgColor: "bg-red-50 border-red-100",
    },
    {
      label: "Client Satisfaction",
      value: formatValue(
        performance.client_satisfaction,
        (val) => `${Number(val).toFixed(2)}/5`,
      ),
      icon: Star,
      color: "text-amber-600",
      bgColor: "bg-amber-50 border-amber-100",
    },
    {
      label: "Repeat Client Rate",
      value: formatValue(
        performance.repeat_client_rate,
        (val) => `${Number(val).toFixed(1)}%`,
      ),
      icon: Users,
      color: "text-ink",
      bgColor: "bg-surface-muted border-border",
    },
    {
      label: "Service Efficiency",
      value: formatValue(
        performance.service_efficiency,
        (val) => `${Number(val).toFixed(1)}%`,
      ),
      icon: Zap,
      color: "text-orange-600",
      bgColor: "bg-orange-50 border-orange-100",
    },
  ];

  const cardClass = "product-panel p-4 sm:p-6";

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Performance"
        subtitle="Completion, cancellations, and repeat rates across Internal and External channels."
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.performance.title}
        steps={VENDOR_PAGE_GUIDES.performance.steps}
        icon={ListChecks}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div
          className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
          role="tablist"
          aria-label="Performance period"
        >
          {PERIOD_TABS.map((tab) => {
            const isActive = period === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setPeriod(tab.value)}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${isActive ? "bg-surface text-ink shadow-sm" : "text-ink-secondary hover:text-gray-900"}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <ChannelFilterTabs value={channel} onChange={setChannel} />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className={`${cardClass} animate-pulse`}>
              <div className="h-4 bg-gray-200 rounded w-24 mb-4" />
              <div className="h-8 bg-gray-200 rounded w-32" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm">
          Failed to load performance data. Please try again.
        </div>
      ) : (
        <>
          {channel === "all" && (
            <ChannelSplitCards byChannel={performance.by_channel} />
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {performanceMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className={cardClass}>
                  <div
                    className={`w-12 h-12 border ${metric.bgColor} rounded-xl flex items-center justify-center mb-4`}
                  >
                    <Icon className={`w-6 h-6 ${metric.color}`} />
                  </div>
                  <p className="text-sm text-ink-secondary mb-2">
                    {metric.label}
                  </p>
                  <p className="text-2xl font-bold tracking-tight text-ink tabular-nums">
                    {metric.value}
                  </p>
                </div>
              );
            })}
          </div>

          {performance.peak_hours && performance.peak_hours.length > 0 && (
            <div className={cardClass}>
              <h2 className="text-lg font-semibold text-ink mb-4">
                Peak Hours
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {performance.peak_hours.map((hour, index) => (
                  <div
                    key={index}
                    className="text-center rounded-xl border border-border bg-surface-muted p-3"
                  >
                    <p className="text-sm text-ink-secondary">{hour.hour}</p>
                    <p className="text-lg font-semibold text-ink tabular-nums">
                      {hour.bookings || 0}
                    </p>
                    <p className="text-xs text-ink-tertiary">bookings</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VendorPerformance;
